import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface Reminder {
  id: string;
  task: string;
  dueDate: string;
  dueTime: string;
  isDone: boolean;
  isUrgent: boolean;
  relatedType: string | null;
  relatedId: string | null;
  createdAt: string;
}

interface DbReminder {
  id: string;
  task: string;
  due_date: string | null;
  due_time: string | null;
  is_done: boolean | null;
  is_urgent: boolean | null;
  related_type: string | null;
  related_id: string | null;
  created_at: string;
}

function mapReminder(row: DbReminder): Reminder {
  return {
    id: row.id,
    task: row.task,
    dueDate: row.due_date || '',
    dueTime: row.due_time || '',
    isDone: row.is_done || false,
    isUrgent: row.is_urgent || false,
    relatedType: row.related_type || null,
    relatedId: row.related_id || null,
    createdAt: row.created_at,
  };
}

export function useReminders() {
  const { user } = useAuth();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReminders = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from('ep_reminders')
      .select('*')
      .eq('user_id', user.id)
      .order('due_date', { ascending: true })
      .order('due_time', { ascending: true });

    if (dbError) {
      setError(dbError.message);
      setIsLoading(false);
      return;
    }

    setReminders((data || []).map(mapReminder));
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const addReminder = useCallback(
    async (form: {
      task: string;
      dueDate: string;
      dueTime: string;
      isUrgent?: boolean;
      relatedType?: string;
      relatedId?: string;
    }): Promise<{ error: string | null; reminder?: Reminder }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbRow = {
        user_id: user.id,
        task: form.task,
        due_date: form.dueDate,
        due_time: form.dueTime,
        is_done: false,
        is_urgent: form.isUrgent || false,
        related_type: form.relatedType || null,
        related_id: form.relatedId || null,
      };

      const { data, error: dbError } = await supabase
        .from('ep_reminders')
        .insert(dbRow)
        .select()
        .single();

      if (dbError || !data) {
        return { error: dbError?.message || 'Ekleme başarısız oldu.' };
      }

      const newItem = mapReminder(data as DbReminder);
      setReminders((prev) => {
        const next = [...prev, newItem];
        next.sort((a, b) => {
          const d = a.dueDate.localeCompare(b.dueDate);
          return d !== 0 ? d : a.dueTime.localeCompare(b.dueTime);
        });
        return next;
      });
      return { error: null, reminder: newItem };
    },
    [user]
  );

  const toggleDone = useCallback(
    async (id: string, done: boolean): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const { error: dbError } = await supabase
        .from('ep_reminders')
        .update({ is_done: !done })
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setReminders((prev) => prev.map((r) => (r.id === id ? { ...r, isDone: !done } : r)));
      return { error: null };
    },
    [user]
  );

  const updateReminder = useCallback(
    async (id: string, updates: Partial<Reminder>): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbUpdates: Record<string, unknown> = {};
      if (updates.task !== undefined) dbUpdates.task = updates.task;
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate;
      if (updates.dueTime !== undefined) dbUpdates.due_time = updates.dueTime;
      if (updates.isDone !== undefined) dbUpdates.is_done = updates.isDone;
      if (updates.isUrgent !== undefined) dbUpdates.is_urgent = updates.isUrgent;

      const { error: dbError } = await supabase
        .from('ep_reminders')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setReminders((prev) =>
        prev
          .map((r) => (r.id === id ? { ...r, ...updates } : r))
          .sort((a, b) => {
            const d = a.dueDate.localeCompare(b.dueDate);
            return d !== 0 ? d : a.dueTime.localeCompare(b.dueTime);
          })
      );
      return { error: null };
    },
    [user]
  );

  const deleteReminder = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const { error: dbError } = await supabase
        .from('ep_reminders')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      setReminders((prev) => prev.filter((r) => r.id !== id));
      return { error: null };
    },
    [user]
  );

  return {
    reminders,
    isLoading,
    error,
    refresh: fetchReminders,
    addReminder,
    toggleDone,
    updateReminder,
    deleteReminder,
  };
}